import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { fieldMappingApi } from 'services'
import type {
  FieldMappingConfig,
  SaveFieldMappingsPayload,
  UnifiedFieldMappingConfig,
  SaveUnifiedFieldMappingsPayload,
} from 'services/fieldMappingApi'

const configKey = (provider: string) => ['field-mapping', provider, 'config']
const catalogKey = (provider: string) => ['field-mapping', provider, 'catalog']
const unifiedConfigKey = ['field-mapping', 'unified', 'config']
const unifiedCatalogKey = ['field-mapping', 'unified', 'catalog']

/** The connection's saved mappings + bidirectional flag. */
export const useFieldMappingConfig = (provider: string, enabled = true) =>
  useQuery({
    queryKey: configKey(provider),
    queryFn: () => fieldMappingApi.getConfig(provider),
    enabled: enabled && !!provider,
    retry: false,
  })

/** Vera field catalog (static) + source fields discovered from the store. */
export const useFieldMappingCatalog = (provider: string, enabled = true) =>
  useQuery({
    queryKey: catalogKey(provider),
    queryFn: () => fieldMappingApi.getCatalog(provider),
    enabled: enabled && !!provider,
    retry: false,
    staleTime: 60_000,
  })

/** Atomically replace the connection's mapping set. */
export const useSaveFieldMapping = (provider: string) => {
  const queryClient = useQueryClient()
  return useMutation<FieldMappingConfig, Error, SaveFieldMappingsPayload>({
    mutationFn: (payload) => fieldMappingApi.saveConfig(provider, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(configKey(provider), data)
      queryClient.invalidateQueries({ queryKey: configKey(provider) })
    },
  })
}

// ---- VRG-100 — unified cross-integration hooks ----

/** Aggregated mappings across every connected integration. */
export const useUnifiedFieldMappingConfig = (enabled = true) =>
  useQuery({
    queryKey: unifiedConfigKey,
    queryFn: () => fieldMappingApi.getUnifiedConfig(),
    enabled,
    retry: false,
  })

/** Vera catalog + source fields across every connected integration (each badged by provider). */
export const useUnifiedFieldMappingCatalog = (enabled = true) =>
  useQuery({
    queryKey: unifiedCatalogKey,
    queryFn: () => fieldMappingApi.getUnifiedCatalog(),
    enabled,
    retry: false,
    staleTime: 60_000,
  })

/** Fan a unified save out per provider. */
export const useSaveUnifiedFieldMapping = () => {
  const queryClient = useQueryClient()
  return useMutation<UnifiedFieldMappingConfig, Error, SaveUnifiedFieldMappingsPayload>({
    mutationFn: (payload) => fieldMappingApi.saveUnified(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(unifiedConfigKey, data)
      queryClient.invalidateQueries({ queryKey: unifiedConfigKey })
    },
  })
}
